import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type {
  BatchUpdateTasksInput,
  CreateTaskInput,
  TaskPriority,
  TaskResponse,
  UpdateTaskInput,
} from '@erp/rpc';

const PRIORITY_WEIGHT: Record<TaskPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function sortByPriorityThenUpdatedAt(a: TaskResponse, b: TaskResponse): number {
  const pa = PRIORITY_WEIGHT[a.priority] ?? 4;
  const pb = PRIORITY_WEIGHT[b.priority] ?? 4;
  if (pa !== pb) return pa - pb;
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}

export interface GroupedTasks {
  active: TaskResponse[];
  queue: TaskResponse[];
  needsInput: TaskResponse[];
  done: TaskResponse[];
  completed: TaskResponse[];
  failed: TaskResponse[];
  cancelled: TaskResponse[];
}

export function agentTasksQueryKey(agentDefinitionId?: string) {
  return ['agent-tasks', agentDefinitionId];
}

export function useListAgentTasks(agentDefinitionId?: string) {
  const query = useQuery({
    queryKey: agentTasksQueryKey(agentDefinitionId),
    queryFn: async () => {
      if (!agentDefinitionId)
        throw new Error('Agent definition ID is required');
      return rpcClient.tasks.list({
        assigneeAgentDefinitionId: agentDefinitionId,
        // A needs_input escalation reassigns the task to a user; without this
        // the task drops out of the agent's board entirely and the Needs Input
        // tab is always empty.
        includeEscalatedFromAgent: true,
        limit: 100,
        offset: 0,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      });
    },
    enabled: !!agentDefinitionId,
    refetchInterval: (query) => {
      const tasks = query.state.data?.tasks ?? [];
      // needs_input counts as active: the task is waiting on a human and can
      // return to the queue at any moment (answered from the inbox).
      const hasActiveTasks = tasks.some(
        (t) =>
          t.status === 'pending' ||
          t.status === 'in_progress' ||
          t.status === 'needs_input',
      );
      return hasActiveTasks ? 5000 : false;
    },
  });

  const tasks = query.data?.tasks ?? [];

  const grouped: GroupedTasks = {
    active: tasks
      .filter((t) => t.status === 'in_progress')
      .sort(sortByPriorityThenUpdatedAt),
    queue: tasks
      .filter((t) => t.status === 'pending')
      .sort(sortByPriorityThenUpdatedAt),
    needsInput: tasks
      .filter((t) => t.status === 'needs_input')
      .sort(sortByPriorityThenUpdatedAt),
    done: tasks.filter(
      (t) =>
        t.status === 'completed' ||
        t.status === 'failed' ||
        t.status === 'cancelled',
    ),
    completed: tasks.filter((t) => t.status === 'completed'),
    failed: tasks.filter((t) => t.status === 'failed'),
    cancelled: tasks.filter((t) => t.status === 'cancelled'),
  };

  return { ...query, grouped };
}

/**
 * Server-side open-task counts for an agent's page tile: its own running /
 * queued / needs-input tasks, plus needs-input tasks it handed off to a person.
 */
export function useAgentOpenTaskCounts(agentDefinitionId?: string) {
  return useQuery({
    ...orpc.tasks.agentOpenCounts.queryOptions({
      input: { agentDefinitionId: agentDefinitionId ?? '' },
    }),
    enabled: !!agentDefinitionId,
    // Same cadence as the board's poll so the tile and the list agree.
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });
}

interface UseAgentTaskMutationOptions {
  agentDefinitionId?: string;
}

function useInvalidateAgentTasks(agentDefinitionId?: string) {
  const queryClient = useQueryClient();

  return function invalidateAgentTasks() {
    void queryClient.invalidateQueries({
      queryKey: agentTasksQueryKey(agentDefinitionId),
    });
  };
}

export function useCreateAgentTask({
  agentDefinitionId,
}: UseAgentTaskMutationOptions) {
  const invalidateAgentTasks = useInvalidateAgentTasks(agentDefinitionId);

  return useMutation({
    mutationFn: async (input: CreateTaskInput) => rpcClient.tasks.create(input),
    onSuccess: invalidateAgentTasks,
  });
}

export function useUpdateAgentTask({
  agentDefinitionId,
}: UseAgentTaskMutationOptions) {
  const invalidateAgentTasks = useInvalidateAgentTasks(agentDefinitionId);

  return useMutation({
    mutationFn: async (input: UpdateTaskInput) => rpcClient.tasks.update(input),
    onSuccess: invalidateAgentTasks,
  });
}

export function useDeleteAgentTask({
  agentDefinitionId,
}: UseAgentTaskMutationOptions) {
  const invalidateAgentTasks = useInvalidateAgentTasks(agentDefinitionId);

  return useMutation({
    mutationFn: async ({ id }: { id: string }) =>
      rpcClient.tasks.delete({ id }),
    onSuccess: invalidateAgentTasks,
  });
}

export function useBulkDeleteAgentTasks({
  agentDefinitionId,
}: UseAgentTaskMutationOptions) {
  const invalidateAgentTasks = useInvalidateAgentTasks(agentDefinitionId);

  return useMutation({
    mutationFn: async ({ ids }: { ids: string[] }) => {
      const results = await Promise.allSettled(
        ids.map((id) => rpcClient.tasks.delete({ id })),
      );
      const failed = results.filter((result) => result.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(
          `Failed to delete ${failed.length} of ${ids.length} tasks`,
        );
      }
    },
    onSettled: invalidateAgentTasks,
  });
}

/**
 * Cancel each task through the single-task terminal path with a reason and
 * no outcome — the reason-only cancellation every typed task accepts.
 * `batchUpdate` can't do this: it carries no reason, so it refuses to close a
 * task whose type declares an outcome vocabulary.
 */
export function useBulkCancelAgentTasks({
  agentDefinitionId,
}: UseAgentTaskMutationOptions) {
  const invalidateAgentTasks = useInvalidateAgentTasks(agentDefinitionId);

  return useMutation({
    mutationFn: async ({ ids, reason }: { ids: string[]; reason: string }) => {
      const results = await Promise.allSettled(
        ids.map((id) => rpcClient.tasks.cancel({ id, outcomeReason: reason })),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(
          `Failed to cancel ${failed.length} of ${ids.length} tasks`,
        );
      }
    },
    onSettled: invalidateAgentTasks,
  });
}

export function useResetAgentTaskStatus({
  agentDefinitionId,
}: UseAgentTaskMutationOptions) {
  const invalidateAgentTasks = useInvalidateAgentTasks(agentDefinitionId);

  return useMutation({
    mutationFn: async ({
      taskIds,
    }: {
      taskIds: BatchUpdateTasksInput['taskIds'];
    }) =>
      rpcClient.tasks.batchUpdate({
        taskIds,
        status: 'pending',
      }),
    onSuccess: invalidateAgentTasks,
  });
}

export function useBulkSetPriorityAgentTasks({
  agentDefinitionId,
}: UseAgentTaskMutationOptions) {
  const invalidateAgentTasks = useInvalidateAgentTasks(agentDefinitionId);

  return useMutation({
    mutationFn: async ({
      ids,
      priority,
    }: {
      ids: string[];
      priority: TaskPriority;
    }) => {
      const results = await Promise.allSettled(
        ids.map((id) => rpcClient.tasks.update({ id, priority })),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(
          `Failed to update ${failed.length} of ${ids.length} tasks`,
        );
      }
    },
    onSettled: invalidateAgentTasks,
  });
}

export function useBulkEditDescriptionAgentTasks({
  agentDefinitionId,
}: UseAgentTaskMutationOptions) {
  const invalidateAgentTasks = useInvalidateAgentTasks(agentDefinitionId);

  return useMutation({
    mutationFn: async ({
      ids,
      description,
    }: {
      ids: string[];
      description: string | null;
    }) => {
      const results = await Promise.allSettled(
        ids.map((id) => rpcClient.tasks.update({ id, description })),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(
          `Failed to update ${failed.length} of ${ids.length} tasks`,
        );
      }
    },
    onSettled: invalidateAgentTasks,
  });
}

// ── User-assigned tasks ──────────────────────────────────────────────────

export function userTasksQueryKey(userId?: string) {
  return ['user-tasks', userId];
}

export function useUserTasks(userId?: string) {
  const query = useQuery({
    queryKey: userTasksQueryKey(userId),
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      return rpcClient.tasks.list({
        assigneeUserId: userId,
        statuses: ['pending', 'in_progress'],
        limit: 20,
        offset: 0,
        sortBy: 'priority',
        sortOrder: 'asc',
      });
    },
    enabled: !!userId,
  });

  const tasks = query.data?.tasks ?? [];

  return {
    tasks: [...tasks].sort(sortByPriorityThenUpdatedAt),
    total: query.data?.pagination.total ?? 0,
    isLoading: query.isLoading,
    error: query.error,
  };
}
